import React, { useState, useEffect } from 'react';
import { Helmet as MetaTags } from 'react-helmet';
import {
  navigate,
  RouteComponentProps,
  useLocation,
  WindowLocation,
} from '@gatsbyjs/reach-router';
import { Auth } from '~/amplify';
import { Body, Heading1 } from '~/components/atom/Typography';
import { Form, Field } from '~/components/organism/Forms';
import OTPInput from '~/components/molecule/OTPInput';
import JustificationContainer from '~/components/atom/JustificationContainer';
import Button from '~/components/atom/Button';
import TextButton from '~/components/atom/TextButton';
import AuthFormWrapper from '../components/AuthFormWrapper';
import createPageTitle from '~/util/createPageTitle';
import styled, { css } from 'styled-components';
import { reporter } from '~/hooks/useErrorReporter';
import Loading from '~/components/atom/Loading';
import Catalog from '~/Catalog';
import { PROFILE_PATH } from '../../Settings/Profile/constants';
import { AUTH_PATHS } from '../constants';
import { isExpiredOrNotAuthorizedSessionError } from '~/Errors';
import useDHFlag from '~/hooks/useDHFlag';
import TOTPSetupInstructions from '~/components/atom/TOTPSetupInstructions';
import InfoBlock from '~/components/molecule/InfoBlock';
import { useUserProfileSyncMutation } from '~/graphql/types';
import useGetCurrentAccount from '../../AccountSelectionWrapper/hooks/useGetCurrentAccount';
import updateMfaPreferenceAndSyncUserProfile from '~/amplify/utils/updateMfaPreferenceAndSyncUserProfile';
import hasValue from '~/util/hasValue';

const text = {
  title: 'Tweestaps\u00ADverificatie instellen',
  submitButton: 'Code verifiëren',
  errorMessage: 'Ongeldige verificatiecode. Probeer het opnieuw.',
  successMessage: 'Tweestaps\u00ADverificatie succesvol ingesteld',
  noUserError:
    'Tweestaps\u00ADverificatie instellen mislukt. Probeer het opnieuw.',
  noQRCode:
    'Geen QR-code gevonden om tweestaps\u00ADverificatie in te stellen voor deze gebruiker.',
  goBackToLogin: 'Terug naar inlogpagina',
  code: 'Code',
  noAccountError:
    'Geen account gevonden om tweestapsverificatie in te stellen.',
};

type WindowState = {
  from?: string;
  message?: string;
};

const MFASetupTOTP: React.FC<RouteComponentProps> = () => {
  const isMfaEnabled = useDHFlag('mfa');

  const location = useLocation() as WindowLocation<WindowState>;
  const [error, setError] = useState<string | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [pageLoading, setPageLoading] = useState(true);

  const result = useGetCurrentAccount();
  const accountId = result?.accountId;

  const [userProfileSync, { loading: updateLoading }] =
    useUserProfileSyncMutation({
      onError: error => {
        reporter.captureException(
          new Error(`MFASetupTOTP: Failed to userProfileSync: ${error}`),
          'debug',
        );
      },
    });

  useEffect(() => {
    if (!isMfaEnabled) return;

    const setupMFA = async () => {
      if (!hasValue(accountId)) {
        reporter.captureException(
          new Error('MFASetupTOTP: No accountId found in setupMFA fn'),
          'debug',
        );
        return;
      }

      try {
        // Make sure it is up to date with backend
        await userProfileSync({ variables: { accountId } });

        // Check if MFA is already enabled
        const mfaPreferences = await Auth.fetchMFAPreference();
        const isMFAAlreadyEnabled =
          mfaPreferences.enabled && mfaPreferences.enabled.length > 0;

        if (isMFAAlreadyEnabled) {
          void navigate(PROFILE_PATH, { replace: true });
          return;
        }

        // Set up MFA
        const currentUser = await Auth.getCurrentUser();
        const result = await Auth.setUpTOTP();
        const cognitoId = currentUser.username;

        setQrCode(
          `otpauth://totp/AWS:${cognitoId}?secret=${result.sharedSecret}&issuer=AWS`,
        );
        setPageLoading(false);
      } catch (err) {
        setPageLoading(false);
        reporter.captureMessage(`MFA: Failed to setUpTOTP: ${err}`, 'error');
        setError(text.noUserError);
      }
    };

    void setupMFA();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMfaEnabled, location.state?.from, accountId]);

  if (!isMfaEnabled) return <>MFA feature flag is not enabled</>;

  const onFormSubmit = async ({
    code,
    accountId,
  }: {
    code: string;
    accountId: string;
  }) => {
    setError(null);

    try {
      await Auth.verifyTOTPSetup({ code });
      await updateMfaPreferenceAndSyncUserProfile(
        { totp: 'ENABLED' },
        async () => await userProfileSync({ variables: { accountId } }),
      );

      const [attributes, mfaPreferences] = await Promise.all([
        Auth.fetchUserAttributes(),
        Auth.fetchMFAPreference(),
      ]);
      const hasVerifiedPhone =
        attributes.phone_number != null &&
        attributes.phone_number_verified === 'true';
      const smsEnabled = mfaPreferences.enabled?.includes('SMS') ?? false;

      if (hasVerifiedPhone && smsEnabled) {
        void navigate('/-/', { replace: true });
        return;
      }

      void navigate(AUTH_PATHS.MFA_SETUP_PHONE, {
        state: {
          from: location.state?.from,
        },
        replace: true,
      });
      return;
    } catch (err) {
      if (isExpiredOrNotAuthorizedSessionError(err)) {
        void navigate(AUTH_PATHS.LOGIN, {
          replace: true,
          state: { message: Catalog.sessionExpired },
        });
        return;
      }
      reporter.captureMessage(
        `MFA: Failed to verifyTOTPSetup: ${err}`,
        'error',
      );
      setError(text.errorMessage);
    }
  };

  if (!hasValue(accountId)) {
    return (
      <>
        <MetaTags>
          <title>{createPageTitle(text.title)}</title>
        </MetaTags>
        <Body>{text.noAccountError}</Body>
      </>
    );
  }

  if (pageLoading) {
    return (
      <>
        <MetaTags>
          <title>{createPageTitle(text.title)}</title>
        </MetaTags>
        <Loading />
      </>
    );
  }

  return (
    <>
      <MetaTags>
        <title>{createPageTitle(text.title)}</title>
      </MetaTags>
      <AuthFormWrapper>
        <Heading1
          lineHeight="base"
          margin={[null, null, 'xxxl', 'xxl']}
          color={{ group: 'accent' }}
          size="xxxl"
          skewed
        >
          {text.title}
        </Heading1>

        {location.state?.message && (
          <InfoBlock
            appearance="info"
            padding={['base']}
            inline
            icon={{
              name: 'info',
              backgroundColor: { group: 'info' },
              color: { group: 'white' },
            }}
            headerText={null}
            margin={[null, null, 'xl']}
          >
            {location.state.message}
          </InfoBlock>
        )}

        {qrCode ? (
          <Form
            initialValues={{ code: '' }}
            onSubmit={({ code }) => onFormSubmit({ code, accountId })}
          >
            {({ handleSubmit, submitting, form }) => (
              <StyledForm onSubmit={handleSubmit}>
                <TOTPSetupInstructions qrCode={qrCode} onAuthPage />

                <Field name="code">
                  {({ input }) => (
                    <OTPInput
                      length={6}
                      value={input.value ?? ''}
                      onChange={input.onChange}
                      onBlur={input.onBlur}
                      disabled={submitting}
                      autoFocus
                      onComplete={() => {
                        void form.submit();
                      }}
                    />
                  )}
                </Field>

                {error && (
                  <Body
                    margin={[null]}
                    color={{ group: 'danger', variant: 'light' }}
                    size="base"
                  >
                    {error}
                  </Body>
                )}

                <Button
                  appearance="primary"
                  size="medium"
                  type="submit"
                  disabled={submitting}
                  loading={submitting || updateLoading}
                  label={text.submitButton}
                />
              </StyledForm>
            )}
          </Form>
        ) : (
          <JustificationContainer
            direction="column"
            gap="xl"
            align="center"
            padding={['l']}
          >
            <Body
              margin={[null]}
              color={{ group: 'white' }}
              size="base"
              align="center"
            >
              {text.noQRCode}
            </Body>
            <TextButton
              appearance="secondary"
              size="medium"
              onClick={() => void navigate(AUTH_PATHS.LOGIN, { replace: true })}
              label={text.goBackToLogin}
              icon="arrowLeft"
              dataTestId="mfa-setup-totp-go-back-to-login-button"
            />
          </JustificationContainer>
        )}
      </AuthFormWrapper>
    </>
  );
};

const StyledForm = styled.form(
  ({ theme }) => css`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${theme.space('xl')};
  `,
);

export default MFASetupTOTP;
